import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { isEqual } from 'lodash';
import {
  IconButton2,
  noop,
  theme,
  TokenizedField,
  Tooltip,
  TooltipTitle,
  useIsAppleOs,
} from '@frameio/vapor';
import {
  IconArrowCounterclockwise,
  IconEdit,
  IconWarningFill,
  IconX,
} from '@frameio/vapor-icons';
import { useLocalOptimism } from '@frameio/vapor-migration/hooks';
import { type CommandConflictInfo, useKeyboardShortcuts } from '../../context';
import type { CommandKeys } from '../../types/CommandKeys';
import {
  displayKeyName,
  isModifierKey,
  isReservedShortcut,
  type ModifierKey,
  validateShortcut,
} from '../../utils/displayKeyName';
import { getCanonicalDisplayOrder } from '../../utils/getKeyboardShortcutString';
import { normalizeKeyFromEvent } from '../../utils/normalizeKey';
import { Keycap } from '../Keycap';
import TEXT from './generated/TEXT/ShortcutCaptureInput';
import styles from './ShortcutCaptureInput.module.css';

TEXT.DEFINE({
  error: {
    /** Error when more than two modifier keys are used */
    maxModifiers: 'Maximum of 2 modifier keys allowed',
    /** Error for reserved shortcuts */
    reservedShortcut: 'This shortcut is reserved by the system or browser',
  },
  placeholder: {
    /** Placeholder while recording a shortcut */
    recording: 'Enter shortcut',
    /** Placeholder when no shortcut is set */
    default: 'Click to enter shortcut',
  },
  ariaLabel: {
    /** Accessible name for the capture field while not recording — it has no
     *  visible label, and the keycaps/recording placeholder don't name it. */
    field: 'Edit keyboard shortcut',
  },
  tooltip: {
    /** Tooltip label to reset shortcut */
    reset: 'Reset shortcut to default',
    /** Tooltip shown when reverting to default would conflict with another
     *  shortcut that has taken these keys. `conflictLabel` is the name of
     *  the command currently bound to those keys; rendered in bold. */
    resetBlocked: ({ conflictLabel }: { conflictLabel: string }) => (
      <>
        {`Can't reset because `}
        <strong>{conflictLabel}</strong>
        {` is using the default keys for this shortcut. Clear or reset that shortcut first.`}
      </>
    ),
    /** Tooltip label to edit shortcut — clears the current value and focuses the input */
    edit: 'Edit',
    /** Tooltip label to clear shortcut binding */
    clear: 'Clear shortcut',
  },
  button: {
    /** Label for reset shortcut button */
    reset: 'Reset',
    /** Label for edit shortcut button */
    edit: 'Edit',
    /** Label for clear shortcut button */
    clear: 'Clear',
  },
  conflict: {
    /** Conflict tooltip — `conflictLabel` is the command currently bound
     *  to the same keys; rendered in bold. */
    inUseBy: ({ conflictLabel }: { conflictLabel: string }) => (
      <>
        {`Shortcut is in use by `}
        <strong>{conflictLabel}</strong>,
        {` clear or change that shortcut first.`}
      </>
    ),
  },
});

// Constants for width calculations
const WIDTH_CONSTANTS = {
  BASE_WIDTH: 30,
  BUTTON_WIDTH: 24,
  // Right-side padding the motion.div grows by when the edit button is
  // revealed: 24 (button) + 4 (gap to chips) + 4 (breathing room). Also
  // serves as the right-side reservation in dynamicWidth, so subtracting
  // HOVER_PADDING_RIGHT from dynamicWidth gives the idle width.
  HOVER_PADDING_RIGHT: 32,
  BASE_SHORTCUT_WIDTH: 24,
  // Width reserved for the empty/cleared placeholder. A bare "—" needs only a
  // glyph's worth of room (the recording state uses RECORDING_DEFAULT_WIDTH).
  EMPTY_PLACEHOLDER_WIDTH: 16,
  RECORDING_DEFAULT_WIDTH: 185,
  // TokenizedField's idle right padding is removed in CSS so the keycaps can
  // align with the row edge. Keep its left padding plus a small glyph buffer
  // so conflict icons sit close without letting keycaps wrap.
  TOKEN_PADDING: 16,
  TOKEN_GAP: 4,
  CHAR_WIDTH_MULTIPLIER: 3, // Additional width per character beyond base
  MODIFIER_WIDTHS: {
    APPLE: {
      Shift: 24,
      Meta: 24,
      Alt: 24,
      Control: 24,
      Ctrl: 24,
    },
    OTHER: {
      Shift: 24,
      Meta: 35,
      Alt: 29,
      Control: 35,
      Ctrl: 35,
    },
  },
} as const;

export type ShortcutCaptureInputHandle = {
  startRecording: () => void;
};

type ShortcutCaptureInputProps = {
  /**
   * Unique identifier for the shortcut being edited.
   * This is the ID of the shortcut in the CommandRegistry (e.g., "viewing.next").
   */
  id: string;

  /**
   * The default key combination for this shortcut, represented as an array of key names.
   * Example: ['Meta', 'Shift', 'ArrowRight']
   * This value is used for display and for resetting the shortcut to its original state.
   */
  defaultValue: string[];

  /**
   * The currently customized key combination for this shortcut, if any, represented as an array of key names.
   * Example: ['Meta', 'Shift', 'ArrowRight']
   * If not provided, the component will use the defaultValue as the current value.
   */
  customizedValue?: string[];

  /**
   * Callback function that is called whenever the shortcut changes.
   * - If the user resets the shortcut, `reset` will be true and `keys` will be undefined.
   * - If the user sets a new shortcut, `keys` will be an array of key names and `reset` will be undefined or false.
   * The `id` is always provided to identify which shortcut was changed.
   * Example usage:
   *   onChange({ id: "save-document", keys: ["Meta", "S"] })
   *   onChange({ id: "save-document", reset: true })
   */
  onChange: (keys: { id: string; keys?: string[]; reset?: boolean }) => void;

  /**
   * Focus the input on mount. Useful when the component is rendered as the
   * result of a user click-to-edit interaction.
   */
  autoFocus?: boolean;

  /**
   * Called when the input enters or leaves recording mode (i.e. focused vs
   * blurred). Lets parents defer visual indicators (e.g. a "Customized" chip)
   * until the user has settled on a value. Persistence is unaffected.
   */
  onRecordingChange?: (isRecording: boolean) => void;

  /**
   * Visual variant.
   * - `default`: original styling, used by the (deprecated) settings page.
   * - `inline`: borderless at rest (so the input reads as plain keycaps in
   *   the help modal's row). On focus/recording the standard rectangle
   *   styling activates via Vapor's `:focus-within` rules.
   */
  variant?: 'default' | 'inline';

  /**
   * When true, shows the edit affordance as if the component itself were
   * hovered. Used by a parent row to extend hover detection to the full row.
   */
  isRowHovered?: boolean;
};

/**
 * A specialized input component for capturing and managing keyboard shortcuts.
 *
 * This component provides an interactive interface for users to:
 * - Record keyboard shortcuts by pressing keys
 * - Validate shortcuts against system/browser reserved combinations
 * - Handle conflicts with existing shortcuts
 * - Reset shortcuts to their default values
 * - Clear custom shortcuts
 *
 * The component supports up to 2 modifier keys (Ctrl, Cmd, Alt, Shift) plus one action key,
 * and provides real-time validation and error feedback.
 *
 * @param props - Component props
 * @param props.id - Unique identifier for the shortcut
 * @param props.defaultValue - Array of key names representing the default shortcut (e.g., ['Meta', 'Shift', 'ArrowRight'])
 * @param props.customizedValue - Array of key names representing the current customized shortcut (optional)
 * @param props.onChange - Callback function called when the shortcut changes
 *
 * @example
 * ```tsx
 * <ShortcutCaptureInput
 *   id="viewing.next"
 *   defaultValue={['Meta', 'ArrowRight']}
 *   customizedValue={['Meta', 'Shift', 'ArrowRight']}
 *   onChange={({ id, keys, reset }) => {
 *     if (reset) {
 *       // Handle reset to default
 *     } else if (keys) {
 *       // Handle new shortcut keys
 *     }
 *   }}
 * />
 * ```
 */
export const ShortcutCaptureInput = forwardRef<
  ShortcutCaptureInputHandle,
  ShortcutCaptureInputProps
>(function ShortcutCaptureInput(
  {
    id,
    defaultValue = [],
    customizedValue,
    onChange,
    autoFocus = false,
    variant = 'default',
    onRecordingChange,
    isRowHovered,
  },
  ref
) {
  const { checkWouldConflict, checkResetConflicts } = useKeyboardShortcuts();
  const [isRecording, setIsRecording] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [chipGroupWidth, setChipGroupWidth] = useState(0);
  const shortcutControlRef = useRef<HTMLDivElement>(null);
  // Use value-equality (not the default `!==`) so a fresh `defaultValue`
  // array reference from the parent — which we get every render because
  // the row passes `[...defaultKeys]` — doesn't blow away the optimistic
  // local state. Without this, clicking the pencil on an uncustomized row
  // would immediately resurrect the default value as the source of truth
  // re-syncs, defeating clearKeys' visual reset.
  const [value, setValue] = useLocalOptimism(
    customizedValue ?? defaultValue,
    (prev, next) => !isEqual(prev, next)
  );
  const [error, setError] = useState<string | null>(null);
  const [pendingConflict, setPendingConflict] = useState<{
    keys: string[];
    conflicts: CommandConflictInfo[];
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const shouldFocusAfterEditRef = useRef(false);
  const isApple = useIsAppleOs() ?? false;

  // Holds the value the user had at the moment they entered edit mode via
  // the pencil button. Lets Escape revert to that value without ever
  // persisting the empty/intermediate state the pencil click introduced.
  // Cleared whenever a value is committed (saveShortcut) or the user
  // confirms the clear by blurring without typing anything.
  const editStashRef = useRef<string[] | null>(null);

  /**
   * Memoized array of non-empty key tokens currently in the shortcut value.
   * Used for rendering the visible shortcut keys.
   */
  const visibleTokens = useMemo(
    () => value.filter((token) => token !== ''),
    [value]
  );

  /**
   * Calculates the display width (in pixels) for a given shortcut token.
   *
   * Modifier keys use predefined widths based on platform (Apple/Other).
   * Non-modifier keys use a base width plus an adjustment based on the display text length.
   *
   * @param token - The shortcut key token (e.g., 'Meta', 'Shift', 'A', 'ArrowRight')
   * @returns The width in pixels to render the token
   */
  const getTokenWidth = useCallback(
    (token: string) => {
      const displayText = displayKeyName(isApple, token);

      // Use predefined widths for modifier keys
      if (isModifierKey(token)) {
        const platform = isApple ? 'APPLE' : 'OTHER';
        return (
          WIDTH_CONSTANTS.MODIFIER_WIDTHS[platform][
            token as keyof (typeof WIDTH_CONSTANTS.MODIFIER_WIDTHS)[typeof platform]
          ] ?? WIDTH_CONSTANTS.BASE_SHORTCUT_WIDTH
        );
      }

      // For non-modifier keys, use base width + character-based adjustment
      return (
        WIDTH_CONSTANTS.BASE_SHORTCUT_WIDTH +
        (displayText.length > 1
          ? displayText.length * WIDTH_CONSTANTS.CHAR_WIDTH_MULTIPLIER
          : 0)
      );
    },
    [isApple]
  );

  /**
   * Memoized canonical value of the current shortcut keys,
   * ensuring consistent ordering for comparison and display.
   */
  const canonicalValue = useMemo(
    () => getCanonicalDisplayOrder(value as CommandKeys[]) as string[],
    [value]
  );

  /**
   * Memoized canonical value of the default shortcut keys,
   * ensuring consistent ordering for comparison and display.
   */
  const canonicalDefaultValue = useMemo(
    () => getCanonicalDisplayOrder(defaultValue as CommandKeys[]) as string[],
    [defaultValue]
  );

  /**
   * Calculates the dynamic width of the shortcut input based on the current value.
   *
   * The width is determined by:
   * - Base width for the input container
   * - Indicator for custom shortcuts
   * - Width of the visible shortcut tokens
   * - Gaps between tokens
   * - Empty placeholder width
   * - Padding for the input container
   *
   * @returns The dynamic width in pixels for the shortcut input
   */
  const dynamicWidth = useMemo(() => {
    const baseWidth = WIDTH_CONSTANTS.BASE_WIDTH;

    // Calculate actual token widths based on displayed text
    const tokenWidths = visibleTokens.map((token) => {
      return getTokenWidth(token);
    });

    const totalTokenWidth = tokenWidths.reduce((sum, width) => sum + width, 0);
    const tokenGaps =
      Math.max(0, visibleTokens.length - 1) * WIDTH_CONSTANTS.TOKEN_GAP;

    const emptyPlaceholder =
      value.length === 0 ? WIDTH_CONSTANTS.EMPTY_PLACEHOLDER_WIDTH : 0;

    // Non-inline cleared state (no tokens + customized) renders the reset
    // button on the right *inside* the field, so reserve room for it. The
    // inline variant pins reset outside the field (a trailing flex sibling),
    // so it needs no in-field slot.
    const clearedButtonSlot =
      variant !== 'inline' &&
      canonicalValue.length === 0 &&
      !isEqual(canonicalValue, canonicalDefaultValue)
        ? WIDTH_CONSTANTS.BUTTON_WIDTH
        : 0;

    return Math.max(
      baseWidth,
      // Right-side reservation: matches HOVER_PADDING_RIGHT so idle width
      // (dynamicWidth - HOVER_PADDING_RIGHT) leaves exactly the chip area
      // + left pad. Was 24 (BASE_SHORTCUT_WIDTH), bumped to 32 to also
      // cover sub-pixel chip rendering widths — keycap glyphs like ⇧/⌘
      // measure slightly wider than the 24 MODIFIER_WIDTHS value due to
      // padding + border being box-sized.
      WIDTH_CONSTANTS.HOVER_PADDING_RIGHT +
        totalTokenWidth +
        tokenGaps +
        emptyPlaceholder +
        clearedButtonSlot +
        WIDTH_CONSTANTS.TOKEN_PADDING
    );
  }, [
    canonicalValue,
    canonicalDefaultValue,
    visibleTokens,
    getTokenWidth,
    value.length,
    variant,
  ]);

  const showEditAffordance =
    (isHovered || isFocusWithin || isRowHovered) &&
    visibleTokens.length > 0 &&
    !isRecording;

  // Inline field with no keys, at rest: shows the em-dash token and needs an
  // explicit aria-label (it has neither a visible label nor a placeholder).
  const isClearedInline =
    variant === 'inline' && visibleTokens.length === 0 && !isRecording;

  const targetWidth = useMemo(() => {
    if (isRecording) return WIDTH_CONSTANTS.RECORDING_DEFAULT_WIDTH;
    if (visibleTokens.length > 0) {
      return Math.max(
        WIDTH_CONSTANTS.BASE_WIDTH,
        dynamicWidth - WIDTH_CONSTANTS.HOVER_PADDING_RIGHT
      );
    }
    return dynamicWidth;
  }, [dynamicWidth, isRecording, visibleTokens.length]);

  const validatedValue = useMemo(() => {
    if (!isRecording && value.length > 0 && !validateShortcut(value)) {
      return [];
    }
    return value;
  }, [value, isRecording]);

  // Update value if validation changed it
  useEffect(() => {
    if (validatedValue.length !== value.length) {
      setValue(validatedValue);
    }
  }, [validatedValue, value.length, setValue]);

  // Measure actual chip group width so the button group is always exactly
  // spaces-4 from the chips regardless of per-key width estimation error.
  useEffect(() => {
    if (variant !== 'inline' || !shortcutControlRef.current) return undefined;
    const grid =
      shortcutControlRef.current.querySelector<HTMLElement>('[role="grid"]');
    if (!grid) return undefined;
    const obs = new ResizeObserver(() => {
      setChipGroupWidth(grid.offsetWidth);
    });
    obs.observe(grid);
    return () => obs.disconnect();
  }, [variant]);

  // Conflicts that *would* arise if the user reverted this shortcut to its
  // default right now. Drives the disabled state + tooltip on the reset
  // buttons so we never let revert produce a duplicate binding.
  const revertConflicts = useMemo(
    () => checkResetConflicts([id])[id] ?? [],
    [checkResetConflicts, id]
  );
  const isRevertBlocked = revertConflicts.length > 0;
  const resetTooltipTitle = isRevertBlocked ? (
    <TooltipTitle>
      {TEXT.tooltip.resetBlocked({
        conflictLabel: revertConflicts.map((c) => c.displayLabel).join(', '),
      })}
    </TooltipTitle>
  ) : (
    TEXT.tooltip.reset
  );

  const saveShortcut = useCallback(
    (keys: string[]) => {
      // Committing a value supersedes any pending edit-mode revert: once
      // the user lands on a real combo, Escape shouldn't undo it.
      editStashRef.current = null;
      // If the user landed back on the default combo (e.g. backspaced
      // to it, or re-typed it), persist as a reset rather than a customization
      // — otherwise the store keeps an entry equal to the default and the row
      // shows as customized even though it isn't.
      const canonicalKeys = getCanonicalDisplayOrder(
        keys as CommandKeys[]
      ) as string[];
      if (keys.length > 0 && isEqual(canonicalKeys, canonicalDefaultValue)) {
        onChange({ id, reset: true });
        return;
      }
      onChange({
        id,
        keys,
      });
    },
    [canonicalDefaultValue, id, onChange]
  );

  const resetShortcut = useCallback(() => {
    // Reset is a commit — clear the edit-stash before the blur below so
    // handleBlur's "uncommitted edit blur" branch doesn't fire and persist
    // `{keys: []}` immediately after we just removed the customization.
    // Without this, clicking the in-field reset while still focused/recording
    // produces a stale empty-binding write that races (and beats) the reset.
    editStashRef.current = null;
    setValue(defaultValue);
    onChange({
      id,
      reset: true,
    });
    inputRef.current?.blur();
  }, [defaultValue, id, onChange, setValue]);

  const clearKeys = useCallback(() => {
    // Stash the value the user is leaving so Escape can restore it. We
    // intentionally don't call onChange here — the pencil should be
    // cancellable until the user either commits a new combo (saveShortcut
    // clears the stash) or confirms by blurring without typing anything
    // (handleBlur persists the empty binding).
    editStashRef.current = value;
    setValue([]);
    setError(null);
    shouldFocusAfterEditRef.current = true;
    // Enter recording synchronously so this setState batches with the others
    // above. Otherwise the focus event below queues a second render whose
    // animate.width target (185) differs from this render's (the cleared-state
    // dynamicWidth ~179), giving the width tween a mid-flight target shift
    // that reads as a stutter.
    setIsRecording(true);
  }, [setValue, value]);

  // Returns false and sets pendingConflict instead of saving when a conflict is detected.
  const trySaveWithConflictCheck = useCallback(
    (keys: string[]): boolean => {
      const conflicts = checkWouldConflict(keys, id);
      if (conflicts.length > 0) {
        setPendingConflict({ keys, conflicts });
        return false;
      }
      saveShortcut(keys);
      return true;
    },
    [checkWouldConflict, id, saveShortcut]
  );

  const handleRemoveBinding = useCallback(() => {
    // The clear button uses preventFocusOnPress, so clicking it doesn't move
    // focus. When the user resolves a conflict by clearing the *other* row's
    // shortcut, that other row's input stays focused (recording). Blur it
    // explicitly so it commits/reverts and exits recording.
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== inputRef.current
    ) {
      document.activeElement.blur();
    }
    // Commit an explicit empty binding (different from Reset which reverts to
    // default). Clear the stash first so handleBlur doesn't revert.
    editStashRef.current = null;
    onChange({ id, keys: [] });
    setValue([]);
    inputRef.current?.blur();
  }, [onChange, id, setValue]);

  useImperativeHandle(ref, () => ({
    startRecording: clearKeys,
  }));

  const handleBlur = useCallback(() => {
    // Snapshot error before clearing it — used below to detect reserved shortcuts.
    const currentError = error;
    setIsRecording(false);
    setError(null);

    // If there was a pending conflict, the typed combo was never committed.
    // Always revert — use the stash if set, otherwise fall back to the last
    // committed value from the parent so there's no way to persist a conflict.
    if (pendingConflict !== null) {
      setPendingConflict(null);
      const revertTo = editStashRef.current ?? customizedValue ?? defaultValue;
      editStashRef.current = null;
      setValue(revertTo);
      return;
    }

    // If an edit session ended without committing a new combo (saveShortcut
    // clears the stash on commit), treat blur as a cancel: restore the
    // pre-edit value so clicking away doesn't inadvertently clear the shortcut.
    // Also revert when there is an active error (e.g. a reserved shortcut was
    // typed) since those are never saved.
    if (editStashRef.current !== null) {
      const stash = editStashRef.current;
      editStashRef.current = null;
      if (!validateShortcut(value) || currentError !== null) {
        setValue(stash);
        return;
      }
    }

    if (isEqual(canonicalValue, canonicalDefaultValue)) {
      resetShortcut();
    }
  }, [
    error,
    pendingConflict,
    customizedValue,
    defaultValue,
    canonicalValue,
    canonicalDefaultValue,
    resetShortcut,
    value,
    setValue,
  ]);

  const handleFocus = useCallback(() => {
    setIsRecording(true);
    // Always stash the pre-edit value on focus so Escape and click-out can
    // revert even when the user focuses directly (not via the pencil button).
    if (editStashRef.current === null) {
      editStashRef.current = value;
    }
  }, [value]);

  const handleEscapeKey = useCallback(() => {
    setError(null);
    setPendingConflict(null);
    if (editStashRef.current !== null) {
      // Revert to the value the user had before clicking the pencil. We
      // never persisted the empty/intermediate state, so localStorage
      // already matches the stash — only local state needs to catch up.
      // Clearing the ref *before* blur is important: handleBlur's stash
      // branch is for "user blurred without entering anything"; on Escape
      // we've explicitly handled the revert here.
      setValue(editStashRef.current);
      editStashRef.current = null;
    }
    inputRef.current?.blur();
  }, [setValue]);

  const handleBackspaceKey = useCallback(() => {
    if (value.length === 0) {
      // Nothing to remove. Bailing here also preserves an active edit-mode
      // stash so Escape can still revert a pencil-initiated edit if the
      // user reflexively backspaces in the empty field.
      return;
    }
    const newValue = value.slice(0, -1);
    // Maintain canonical order when removing keys
    const canonicalNewValue = getCanonicalDisplayOrder(
      newValue as CommandKeys[]
    ) as string[];
    setValue(canonicalNewValue);
    setError(null);
    setPendingConflict(null);
    // Only commit when backspacing lands on a valid shorter combo. Backspacing
    // all the way to empty is treated as editing, not a commit: blur reverts to
    // the pre-edit value, so erasing a rejected combo doesn't persist (or track)
    // an empty binding. Disabling a shortcut goes through the explicit Clear
    // button instead.
    if (validateShortcut(canonicalNewValue)) {
      saveShortcut(canonicalNewValue);
    }
  }, [value, setValue, saveShortcut]);

  const handleModifierKey = useCallback(
    (key: string) => {
      const currentModifiers = value.filter(isModifierKey);
      if (currentModifiers.length >= 2) {
        setError(TEXT.error.maxModifiers);
        return false;
      }
      if (currentModifiers.includes(key as ModifierKey)) {
        return false;
      }
      const newValue = [...value, key];
      const canonicalNewValue = getCanonicalDisplayOrder(
        newValue as CommandKeys[]
      ) as string[];
      setValue(canonicalNewValue);
      setError(null);
      return true;
    },
    [value, setValue]
  );

  const handleActionKey = useCallback(
    (key: string) => {
      const modifiers = value.filter(isModifierKey);
      if (value.includes(key)) {
        return false;
      }
      const newValue = [...modifiers, key];

      // Compute canonical order up-front so we can display the value even
      // when the combo is reserved or conflicting.
      const canonicalNewValue = getCanonicalDisplayOrder(
        newValue as CommandKeys[]
      ) as string[];

      if (isReservedShortcut(newValue)) {
        setError(TEXT.error.reservedShortcut);
        // Keep the typed value so the user can see and backspace it.
        // Blur will revert to the pre-edit stash.
        setValue(canonicalNewValue);
        return false;
      }
      setValue(canonicalNewValue);
      setError(null);

      if (
        validateShortcut(canonicalNewValue) &&
        !isEqual(canonicalNewValue, canonicalDefaultValue)
      ) {
        const saved = trySaveWithConflictCheck(canonicalNewValue);
        // Only auto-blur on a clean save; if a conflict was detected, stay
        // in recording mode so the user can backspace and try another combo.
        if (saved) {
          inputRef.current?.blur();
        }
      }
      return true;
    },
    [value, setValue, trySaveWithConflictCheck, canonicalDefaultValue]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Allow Tab to move focus normally always.
      if (event.key === 'Tab') return;

      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'Escape') {
        handleEscapeKey();
        return;
      }

      if (event.key === 'Backspace') {
        handleBackspaceKey();
        return;
      }

      const key = normalizeKeyFromEvent(event);

      if (isModifierKey(key)) {
        handleModifierKey(key);
      } else {
        handleActionKey(key);
      }
    },
    [handleEscapeKey, handleBackspaceKey, handleModifierKey, handleActionKey]
  );

  useEffect(() => {
    const current = inputRef.current;
    if (!current) return;

    current.addEventListener('keydown', handleKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      current.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
    // Only auto-focus on mount; ignore later changes to autoFocus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (
      !shouldFocusAfterEditRef.current ||
      !isRecording ||
      visibleTokens.length > 0
    ) {
      return;
    }

    shouldFocusAfterEditRef.current = false;
    inputRef.current?.focus();
  }, [isRecording, visibleTokens.length]);

  // Notify parents whenever recording state transitions so they can defer
  // visual indicators (e.g. "Customized" chip) until the user blurs.
  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  const handleFocusWithin = useCallback(() => {
    setIsFocusWithin(true);
  }, []);

  const handleBlurWithin = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // relatedTarget is the element receiving focus next. If it's outside
      // the container, focus actually left this shortcut row.
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setIsFocusWithin(false);
      }
    },
    []
  );

  return (
    <div className={styles.outerWrapper}>
      <div
        className={styles.container}
        onBlur={handleBlurWithin}
        onFocus={handleFocusWithin}
      >
        <AnimatePresence>
          {error && (
            <motion.div
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              style={{ height: 24 }}
              transition={{ duration: 0.1, ease: 'easeOut' }}
            >
              {/* Force-open whenever the icon is visible — the error/conflict
                  tooltips are surfaced *because* there's something wrong, so
                  the user shouldn't have to hover the small icon to find out
                  what. */}
              <Tooltip
                delayIn={0}
                isShowing={true}
                placement="left"
                title={error}
              >
                <IconWarningFill color={theme.colors.meta.opaque.red} />
              </Tooltip>
            </motion.div>
          )}
          {!error && pendingConflict && (
            <motion.div
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              style={{ height: 24 }}
              transition={{ duration: 0.1, ease: 'easeOut' }}
            >
              <Tooltip
                delayIn={0}
                isShowing={true}
                placement="left"
                title={
                  <TooltipTitle>
                    {TEXT.conflict.inUseBy({
                      conflictLabel: (pendingConflict?.conflicts ?? [])
                        .map((c) => c.displayLabel)
                        .join(', '),
                    })}
                  </TooltipTitle>
                }
              >
                <IconWarningFill color={theme.colors.meta.opaque.yellow} />
              </Tooltip>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          ref={shortcutControlRef}
          className={styles.shortcutControl}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={
            variant === 'inline'
              ? ({
                  // With no visible tokens (cleared state), the grid still
                  // measures the empty input cell — which would push the
                  // button group leftward. Force 0 so the revert button
                  // sits at the field's right edge, lining up with the
                  // keycap rows above.
                  '--chip-group-width': `${
                    visibleTokens.length > 0 ? chipGroupWidth : 0
                  }px`,
                } as React.CSSProperties)
              : undefined
          }
        >
          <motion.div
            animate={{ width: targetWidth }}
            className={
              [
                variant === 'inline' ? styles.inlineFieldShell : '',
                pendingConflict && variant === 'inline'
                  ? styles.conflictField
                  : '',
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
            initial={{ width: targetWidth }}
            transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
          >
            <TokenizedField
              ref={inputRef}
              // At rest the field has no visible label (just keycaps or a dash
              // token), so name it for screen readers. While recording, the
              // "Enter shortcut" placeholder supplies the name instead.
              aria-label={isRecording ? undefined : TEXT.ariaLabel.field}
              error={error ? ' ' : undefined}
              label=""
              onBlur={handleBlur}
              onChange={noop}
              onFocus={handleFocus}
              onTokenAdd={noop}
              onTokenDelete={noop}
              // Only the recording prompt uses the field's own placeholder; the
              // cleared/empty state renders a dash token instead (see below).
              placeholder={isRecording ? TEXT.placeholder.recording : undefined}
              // In inline variant the field is read-only whenever it isn't
              // recording — both the keyed state (so clicking chips doesn't focus
              // the input) and the cleared state (so it stays borderless and a
              // row click, not a field focus, re-enters recording). The pencil
              // button or a row click is the way back into capture mode.
              readOnly={variant === 'inline' && !isRecording}
              tokens={
                isClearedInline
                  ? // Cleared state: a single em dash rendered as a token so it
                    // right-aligns with the keycaps via the same grid rules,
                    // rather than as a left-pinned placeholder or an overlay.
                    [
                      <span
                        key="cleared-dash"
                        aria-hidden="true"
                        className={styles.clearedDash}
                      >
                        —
                      </span>,
                    ]
                  : visibleTokens.map((token) => (
                      <Keycap
                        key={token}
                        $isHovered={Boolean(isRowHovered) && !isRecording}
                      >
                        {displayKeyName(isApple, token)}
                      </Keycap>
                    ))
              }
              value=""
            />
          </motion.div>
          <AnimatePresence>
            <div className={styles.buttonGroup}>
              {/* Non-inline cleared-state reset (no tokens, differs from default). */}
              {variant !== 'inline' &&
                !isEqual(canonicalValue, canonicalDefaultValue) &&
                canonicalValue.length === 0 && (
                  <motion.div
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <Tooltip delayIn={500} title={resetTooltipTitle}>
                      {/* Wrapper div keeps the tooltip hoverable even when the
                          inner IconButton2 is disabled (disabled buttons don't
                          fire pointer events on their own). */}
                      <div style={{ display: 'inline-flex' }}>
                        <IconButton2
                          icon={<IconArrowCounterclockwise />}
                          isDisabled={isRevertBlocked}
                          label={TEXT.button.reset}
                          onClick={resetShortcut}
                          preventFocusOnPress={true}
                          size="small"
                          variant="tertiary"
                        />
                      </div>
                    </Tooltip>
                  </motion.div>
                )}
              {visibleTokens.length > 0 && !isRecording && (
                <motion.div
                  animate={{
                    opacity: showEditAffordance ? 1 : 0,
                    x: showEditAffordance ? 0 : -6,
                  }}
                  exit={{ opacity: 0, x: -6 }}
                  initial={{ opacity: 0, x: -6 }}
                  style={{
                    pointerEvents: showEditAffordance ? 'auto' : 'none',
                  }}
                  transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
                >
                  <Tooltip delayIn={250} title={TEXT.tooltip.clear}>
                    <IconButton2
                      icon={<IconX />}
                      label={TEXT.button.clear}
                      onClick={handleRemoveBinding}
                      preventFocusOnPress={true}
                      size="small"
                      variant="tertiary"
                    />
                  </Tooltip>
                </motion.div>
              )}
              {visibleTokens.length > 0 && !isRecording && (
                <motion.div
                  animate={{
                    opacity: showEditAffordance ? 1 : 0,
                    x: showEditAffordance ? 0 : -6,
                  }}
                  exit={{ opacity: 0, x: -6 }}
                  initial={{ opacity: 0, x: -6 }}
                  style={{
                    pointerEvents: showEditAffordance ? 'auto' : 'none',
                  }}
                  transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
                >
                  <Tooltip delayIn={250} title={TEXT.tooltip.edit}>
                    <IconButton2
                      icon={<IconEdit />}
                      label={TEXT.button.edit}
                      onClick={clearKeys}
                      preventFocusOnPress={true}
                      size="small"
                      variant="tertiary"
                    />
                  </Tooltip>
                </motion.div>
              )}
            </div>
          </AnimatePresence>
        </div>
        {/* Inline reset is pinned to the right of the keycaps (a trailing flex
            sibling) rather than floating left of them in the button group, so
            its position is constant regardless of how many keycaps render. It
            stays mounted during recording too, so the recording box expands to
            its left instead of replacing it. */}
        {variant === 'inline' && (
          <AnimatePresence>
            {customizedValue !== undefined && (
              <motion.div
                animate={{ opacity: 1, width: 'auto' }}
                className={styles.trailingReset}
                exit={{ opacity: 0, width: 0 }}
                initial={{ opacity: 0 }}
                // Collapse the width on exit (not just fade) so resetting doesn't
                // snap the keycaps sideways when the button leaves — they glide
                // as it closes. Enter stays a plain fade (no expand) so customized
                // rows don't animate sideways when the dialog first opens.
                // overflow:hidden clips the icon during the collapse.
                style={{ overflow: 'hidden' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <Tooltip delayIn={250} title={resetTooltipTitle}>
                  {/* Wrapper div keeps the tooltip hoverable even when the
                      inner IconButton2 is disabled (disabled buttons don't fire
                      pointer events on their own). */}
                  <div style={{ display: 'inline-flex' }}>
                    <IconButton2
                      icon={<IconArrowCounterclockwise />}
                      isDisabled={isRevertBlocked}
                      label={TEXT.button.reset}
                      onClick={resetShortcut}
                      preventFocusOnPress={true}
                      size="small"
                      variant="tertiary"
                    />
                  </div>
                </Tooltip>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
});
